import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../components/BodyCopy";
import { Layout } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { PayoutInfo } from "../components/PayoutInfo";
import { Toast } from "../components/Toast";
import { MailStrokeIcon, WhatsAppStrokeIcon } from "../assets/icons";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { usePartnerStore } from "../store/usePartnerStore";
import { getEvents, safeCapture } from "../analytics";
import { TITLE_COLOR } from "../constants";
import { Navbar } from "../components/Navbar";

export const SupportPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const recoverySupportEmail = usePartnerStore((s) => s.supportEmail);
  const recoveryWhatsappNumber = usePartnerStore(
    (s) => s.supportWhatsappNumber
  );
  const colorConfig = theme.whiteView;
  const { dropzoneColors } = colorConfig;
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    safeCapture(getEvents().SUPPORT_VIEW);
  }, []);

  const whatsappNumber =
    session.data?.partner?.supportWhatsappNumber ?? recoveryWhatsappNumber;
  const supportEmail =
    session.data?.partner?.supportEmail ?? recoverySupportEmail;

  const goToWhatsApp = useCallback(() => {
    if (!whatsappNumber) return;
    const cleanPhone = whatsappNumber.replace(/\D/g, "");
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  }, [whatsappNumber]);

  const sendEmail = useCallback(async () => {
    if (!supportEmail) return;
    try {
      await navigator.clipboard.writeText(supportEmail);
      setShowToast(true);
    } catch {
      // Clipboard may be unavailable; still open mail client.
    }
    window.open(`mailto:${supportEmail}`, "_blank");
  }, [supportEmail]);

  const hasChannels = Boolean(whatsappNumber || supportEmail);

  return (
    <AuraLayout colorConfig={colorConfig}>
      <Layout.Header>
        <Navbar
          onBackClick={() => navigate(-1)}
          title={t("contact.title")}
          align="center"
          color={TITLE_COLOR}
        />
      </Layout.Header>
      <Layout.Content>
        <section className="support-page-v2-container">
          {whatsappNumber && (
            <PayoutInfo
              title={t("contact.whatsapp")}
              subtitle=""
              color={TITLE_COLOR}
              icon={
                <WhatsAppStrokeIcon
                  size={24}
                  colors={dropzoneColors}
                  className="bg-transparent"
                />
              }
              onChange={goToWhatsApp}
            />
          )}
          {supportEmail && (
            <PayoutInfo
              title={t("contact.email")}
              subtitle=""
              color={TITLE_COLOR}
              icon={
                <MailStrokeIcon
                  size={24}
                  colors={dropzoneColors}
                  className="bg-transparent"
                />
              }
              onChange={() => void sendEmail()}
            />
          )}
          {!hasChannels && (
            <BodyCopy className="footer-text muted-foreground">
              {t("contact.empty")}
            </BodyCopy>
          )}
        </section>
        <section className="toast-container">
          {showToast && (
            <Toast
              message={t("contact.emailCopied")}
              duration={2000}
              type="success"
              onClose={() => setShowToast(false)}
            />
          )}
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
